import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Button } from 'react-bootstrap';
import type { TFunction } from 'i18next';
import {
  afterRsLayoutPaint,
  formatRsLocalDateTimeRangeSummary,
  getRsBoardLayoutEl,
  getRsSelectedDayInMonth,
  isRsBoardFormFocused,
  revealRsBookingForm,
  scrollRsCalendarDayIntoView,
  setRsBoardFormFocus,
} from '../../utils/reservationBoardCalendar.util';
import styles from './ReservationBoardCalendar.module.css';

export const RS_BOOKING_DISPLAY_NAME_INPUT_ID = 'rs-booking-display-name';

type RsBookingSubmitBarProps = {
  displayName: string;
  startDateTime: string;
  endDateTime: string;
  selectedResourceNames: string[];
  overlapResourceNames: string[];
  isSubmitting: boolean;
  summaryPulse: boolean;
  /** カレンダー表示中の年月（要約タップで別月の同日へ誤ジャンプしないため） */
  viewYear: number;
  viewMonth: number;
  /** 選択日時の月が非表示のとき、その月へ切り替えてから日セルへ寄せる */
  onRevealSelectionMonth: () => void;
  t: TFunction;
};

function summaryResourceLabel(
  selectedResourceNames: string[],
  hasOverlap: boolean,
  t: TFunction
): string {
  if (hasOverlap) {
    return t('reservation.board.summaryOverlap', { defaultValue: '時間重複' });
  }
  if (selectedResourceNames.length > 0) {
    return selectedResourceNames.join('・');
  }
  return t('reservation.board.summaryNoResource', { defaultValue: '項目未選択' });
}

function submitBlockReason(
  noName: boolean,
  noResource: boolean,
  hasOverlap: boolean,
  t: TFunction
): string | undefined {
  if (noName) {
    return t('reservation.board.summaryNoNameHint', {
      defaultValue: '所属・名前を入力してください',
    });
  }
  if (noResource) {
    return t('reservation.board.resourceRequired', {
      defaultValue: '項目を1つ以上選択してください。',
    });
  }
  if (hasOverlap) {
    return t('reservation.board.summaryOverlapHint', {
      defaultValue: '同じ項目の既存予約と重なっています',
    });
  }
  return undefined;
}

function submitButtonCopy(
  selectedResourceNames: string[],
  blockReason: string | undefined,
  t: TFunction
): { label: string; title?: string } {
  if (selectedResourceNames.length > 1 && !blockReason) {
    return {
      label: t('reservation.board.registerMulti', {
        defaultValue: '{{count}}件を登録',
        count: selectedResourceNames.length,
      }),
      title: t('reservation.board.registerMultiCount', {
        defaultValue: '{{count}}件の項目を同じ時間帯で登録します',
        count: selectedResourceNames.length,
      }),
    };
  }
  const label = t('reservation.board.register', { defaultValue: '登録' });
  return { label, title: blockReason };
}

/** 狭い画面の固定登録バー（要約タップでフォーム⇔カレンダー往復） */
export default function RsBookingSubmitBar({
  displayName,
  startDateTime,
  endDateTime,
  selectedResourceNames,
  overlapResourceNames,
  isSubmitting,
  summaryPulse,
  viewYear,
  viewMonth,
  onRevealSelectionMonth,
  t,
}: RsBookingSubmitBarProps) {
  const noName = !displayName.trim();
  const noResource = selectedResourceNames.length === 0;
  const hasOverlap = overlapResourceNames.length > 0;
  const submitBlocked = noName || noResource || hasOverlap;
  const overnightPrefix = t('reservation.board.overnightPrefix', { defaultValue: '翌' });
  const blockReason = submitBlockReason(noName, noResource, hasOverlap, t);
  const submitCopy = submitButtonCopy(selectedResourceNames, blockReason, t);
  const [guidePulse, setGuidePulse] = useState(false);
  const guidePulseTimerRef = useRef(0);

  const pulseSummary = () => {
    window.clearTimeout(guidePulseTimerRef.current);
    setGuidePulse(true);
    guidePulseTimerRef.current = window.setTimeout(() => setGuidePulse(false), 500);
  };

  useEffect(
    () => () => {
      window.clearTimeout(guidePulseTimerRef.current);
    },
    []
  );

  const jumpSummaryTarget = () => {
    const layout = getRsBoardLayoutEl();

    // 座標判定は境界で誤る。form-focus フラグでトグルする
    if (isRsBoardFormFocused(layout)) {
      setRsBoardFormFocus(false, layout);
      afterRsLayoutPaint(() => {
        const day = getRsSelectedDayInMonth(startDateTime, viewYear, viewMonth);
        if (day == null) {
          onRevealSelectionMonth();
          return;
        }
        scrollRsCalendarDayIntoView(day, { behavior: 'smooth' });
      });
      return;
    }

    revealRsBookingForm({
      focusNameInputId: RS_BOOKING_DISPLAY_NAME_INPUT_ID,
      focusNameIfEmpty: true,
      displayName,
    });
  };

  /** 不足があるときもタップを受け、該当欄へ誘導する（HTML disabled だとクリックが届かない） */
  const guideBlockedSubmit = () => {
    if (!submitBlocked || isSubmitting) return;
    pulseSummary();
    if (noName) {
      revealRsBookingForm({
        focusNameInputId: RS_BOOKING_DISPLAY_NAME_INPUT_ID,
        focusNameIfEmpty: true,
        displayName,
      });
      return;
    }
    revealRsBookingForm({ displayName });
    if (noResource) {
      afterRsLayoutPaint(() => {
        document
          .querySelector<HTMLElement>('[data-rs-resource-list]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  };

  const onSubmitButtonClick = (event: MouseEvent) => {
    if (!submitBlocked) return;
    event.preventDefault();
    guideBlockedSubmit();
  };

  const summaryPulsing = summaryPulse || guidePulse;

  return (
    <div className={styles.sidebarSubmitBar} data-rs-submit-bar>
      {blockReason ? (
        <p className={styles.submitBlockHint} role="status">
          {blockReason}
        </p>
      ) : null}
      <button
        type="button"
        className={`${styles.submitSelectionSummary}${
          submitBlocked ? ` ${styles.submitSelectionSummaryWarn}` : ''
        }${summaryPulsing ? ` ${styles.submitSelectionSummaryPulse}` : ''}`}
        onClick={jumpSummaryTarget}
        aria-label={t('reservation.board.submitSummaryAria', {
          defaultValue: '選択内容を確認。タップでフォームとカレンダーを行き来',
        })}
      >
        <span className={styles.submitSummaryLine}>
          <span className={styles.submitSummaryBody}>
            <span
              className={
                submitBlocked ? styles.submitSummaryResourcesWarn : styles.submitSummaryResources
              }
            >
              {summaryResourceLabel(selectedResourceNames, hasOverlap, t)}
            </span>
            <span className={styles.submitSummarySep} aria-hidden>
              {' · '}
            </span>
            <span className={styles.submitSummaryRange}>
              {formatRsLocalDateTimeRangeSummary(startDateTime, endDateTime, overnightPrefix)}
            </span>
            <span className={styles.submitSummarySep} aria-hidden>
              {' · '}
            </span>
            {displayName.trim() ? (
              <span className={styles.submitSummaryName}>{displayName.trim()}</span>
            ) : (
              <span className={styles.submitSummaryHint}>
                {t('reservation.board.summaryNoName', { defaultValue: '所属・名前なし' })}
              </span>
            )}
          </span>
          <span className={styles.submitSummaryToggleCue} aria-hidden>
            ↕
          </span>
        </span>
      </button>
      <Button
        type="submit"
        variant={submitBlocked ? 'outline-secondary' : 'primary'}
        size="sm"
        disabled={isSubmitting}
        aria-disabled={submitBlocked || isSubmitting}
        aria-label={
          blockReason ? `${submitCopy.label}（${blockReason}）` : submitCopy.label
        }
        className={submitBlocked ? styles.submitVisuallyBlocked : undefined}
        title={submitCopy.title}
        onClick={onSubmitButtonClick}
      >
        {submitCopy.label}
      </Button>
    </div>
  );
}
